import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PALETTE as P } from "../tokens";
import { useAuth } from "../context/AuthContext";
import { authApi } from "../api/auth";
import { practitionerLinkApi } from "../api/practitionerLink";
import { markPendingConsent } from "../utils/practitionerConsent";
import { isNativeIOS } from "../utils/platform";
import { Wordmark } from "../components/Wordmark";

const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  .auth-screen {
    font-family: 'DM Sans', sans-serif;
    background: ${P.bg};
    min-height: 100vh;
    color: ${P.text};
    max-width: 420px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    padding: env(safe-area-inset-top) 0 48px;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .fu  { animation: fadeUp 0.55s ease forwards; opacity: 0; }
  .d1  { animation-delay: 0.05s; }
  .d2  { animation-delay: 0.15s; }
  .d3  { animation-delay: 0.25s; }
  .d4  { animation-delay: 0.35s; }
  .d5  { animation-delay: 0.45s; }
  .d6  { animation-delay: 0.55s; }

  .mode-tab {
    flex: 1; padding: 10px 4px;
    background: none; border: none; cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px; font-weight: 500;
    letter-spacing: 0.06em;
    border-bottom: 2px solid transparent;
    transition: all 0.2s;
    color: ${P.textFaint};
  }
  .mode-tab.active {
    border-bottom: 2px solid ${P.primary};
    color: ${P.text};
  }

  .auth-input {
    width: 100%;
    background: ${P.surface};
    border: 1.5px solid ${P.border};
    border-radius: 14px;
    padding: 14px 18px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 300;
    color: ${P.text};
    outline: none;
    transition: border-color 0.2s;
    -webkit-appearance: none;
  }
  .auth-input:focus { border-color: ${P.primaryMid}; }
  .auth-input::placeholder { color: ${P.textFaint}; font-style: italic; }

  .btn-primary {
    width: 100%; padding: 17px;
    background: ${P.dark}; border: none; border-radius: 16px;
    cursor: pointer;
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 18px; font-style: italic; color: ${P.bg};
    transition: opacity 0.15s;
    letter-spacing: 0.01em;
  }
  .btn-primary:hover:not(:disabled) { opacity: 0.85; }
  .btn-primary:disabled { opacity: 0.45; cursor: default; }

  .btn-oauth {
    width: 100%; padding: 14px 18px;
    background: ${P.surface};
    border: 1.5px solid ${P.border};
    border-radius: 14px;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px; font-weight: 400;
    color: ${P.text};
    display: flex; align-items: center; justify-content: center; gap: 10px;
    transition: all 0.15s;
  }
  .btn-oauth:hover { background: ${P.surfaceAlt}; border-color: ${P.primaryMid}; }
`;

function GoogleIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

export default function AuthPage() {
  // Initial tab can be deep-linked via ?mode=signup (e.g. from the marketing
  // site's "Get started" CTAs). Defaults to "login" for all other entries.
  const [mode, setMode]         = useState(
    () => (new URLSearchParams(window.location.search).get("mode") === "signup" ? "signup" : "login")
  );
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [name, setName]         = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  // Forgot-password subflow — rendered inline below the password input.
  // `forgotSent = true` flips the UI into the confirmation-only state
  // so the user doesn't re-submit and we stop leaking enumeration hints.
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  // One-shot success banner after the reset-password flow redirects
  // back here ("Password updated. Sign in.").
  const [flashMessage, setFlashMessage] = useState("");

  // Invite / referral code capture. A ``?code=GOLF`` deep-link (e.g. the
  // golf-tournament QR) pre-populates and auto-expands the field; sessionStorage
  // is a fallback so a mid-signup refresh doesn't lose the code. Stored
  // uppercase because codes are uppercase; the server re-normalizes anyway.
  const INVITE_CODE_SS_KEY = "haiven_invite_code";
  const [inviteCode, setInviteCode] = useState("");
  const [codeOpen, setCodeOpen] = useState(false);
  // ``codeApplying`` holds the auto-redirect while we POST /auth/apply-code
  // right after account creation; ``codeFailed`` keeps us on this page to
  // show the non-blocking "you'll start with a trial" banner. Either being
  // truthy suppresses the effect-driven redirect below (see it for why).
  const [codeApplying, setCodeApplying] = useState(false);
  const [codeFailed, setCodeFailed] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  useEffect(() => {
    const urlCode = new URLSearchParams(window.location.search).get("code");
    let stored = null;
    try { stored = sessionStorage.getItem(INVITE_CODE_SS_KEY); } catch { /* private mode */ }
    const code = (urlCode || stored || "").trim().toUpperCase();
    if (code) {
      setInviteCode(code);
      setCodeOpen(true);
      setMode("signup");
      try { sessionStorage.setItem(INVITE_CODE_SS_KEY, code); } catch { /* private mode */ }
    }
  }, []);

  // Flow 1 — carried here from InvitePage (?invite=<token>), which
  // already showed the practitioner's identity and what they'd
  // request. sessionStorage survives a mid-flow refresh the same way
  // the access-code capture above does. ?email= prefills the field
  // from the invite record so the visitor isn't retyping it.
  const INVITE_TOKEN_SS_KEY = "haiven_invite_token";
  const [inviteToken, setInviteTokenState] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlInvite = params.get("invite");
    let stored = null;
    try { stored = sessionStorage.getItem(INVITE_TOKEN_SS_KEY); } catch { /* private mode */ }
    const invite = (urlInvite || stored || "").trim();
    if (invite) {
      setInviteTokenState(invite);
      try { sessionStorage.setItem(INVITE_TOKEN_SS_KEY, invite); } catch { /* private mode */ }
    }
    const urlEmail = params.get("email");
    if (urlEmail) setEmail(urlEmail);
  }, []);

  function clearInviteToken() {
    try { sessionStorage.removeItem(INVITE_TOKEN_SS_KEY); } catch { /* private mode */ }
  }

  const { user, login, signup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Surface any flash message passed via navigate() state (e.g. from
  // the reset-password success redirect). Read-once, then clear the
  // state so it doesn't replay on refresh.
  useEffect(() => {
    if (location.state?.flash) {
      setFlashMessage(location.state.flash);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  function destination(u) {
    if (u?.onboarding_completed_at) return "/dashboard";
    if (u?.first_login_seen) return "/dashboard";  // legacy compat
    return "/onboarding";
  }

  useEffect(() => {
    // Hold the auto-redirect while a code redemption is in flight, or
    // once it has failed (so the trial banner stays visible). handleSubmit
    // drives navigation explicitly in those paths.
    if (user && !codeApplying && !codeFailed) {
      navigate(destination(user), { replace: true });
    }
  }, [user, codeApplying, codeFailed, navigate]);

  function clearInviteCode() {
    try { sessionStorage.removeItem(INVITE_CODE_SS_KEY); } catch { /* private mode */ }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(email, password);
        if (inviteToken) {
          try {
            const tok = localStorage.getItem("haven_token");
            await practitionerLinkApi.redeemInvite(inviteToken, tok);
            clearInviteToken();
            markPendingConsent();
          } catch {
            // Non-blocking, same posture as the code-redemption failure
            // below — the account is real and signed in either way.
            clearInviteToken();
          }
        }
        navigate(destination(u), { replace: true });
        return;
      }

      // Signup. Capture the code/invite BEFORE creating the account so
      // we can redeem it immediately after. Mutually exclusive entry
      // points in practice (a visitor arrives via one link or the
      // other), so at most one of these branches fires.
      const code = (inviteCode || "").trim();
      if (code) setCodeApplying(true);
      const u = await signup(email, password, name);

      if (code) {
        try {
          // signup() persisted the token synchronously to localStorage.
          const tok = localStorage.getItem("haven_token");
          const result = await authApi.applyCode(code, tok);
          // Flow 3: a practitioner-originated code auto-created the
          // link (still unconsented) server-side — open the same
          // consent dialog Flow 1 uses, first time Home mounts.
          if (result?.practitioner_link_id) markPendingConsent();
          clearInviteCode();
          setCodeApplying(false);
          // Golf-cohort fix: redemption used to succeed SILENTLY — the
          // one thing an event signup wants to know ("did my code work?")
          // never appeared. Onboarding's first screen acknowledges it.
          navigate(destination(u), { replace: true, state: { codeApplied: true } });
        } catch {
          // Non-blocking: the account exists and the user is signed in;
          // the code just didn't apply. Keep them here to show the banner.
          clearInviteCode();
          setCodeApplying(false);
          setCodeFailed(true);
          setPendingUser(u);
        }
        return;
      }

      if (inviteToken) {
        try {
          const tok = localStorage.getItem("haven_token");
          await practitionerLinkApi.redeemInvite(inviteToken, tok);
          clearInviteToken();
          markPendingConsent();
        } catch {
          clearInviteToken();
        }
        navigate(destination(u), { replace: true });
        return;
      }

      navigate(destination(u), { replace: true });
    } catch (err) {
      setCodeApplying(false);
      setError("Something didn\u2019t work. Try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotSubmit(e) {
    e.preventDefault();
    if (forgotSending || forgotSent) return;
    setForgotSending(true);
    try {
      await authApi.forgotPassword(forgotEmail || email);
    } catch {
      // Silent — backend already returns 200 for unknown emails to
      // prevent enumeration; the only way to land here is a genuine
      // network error, and even then we show the same confirmation.
    } finally {
      setForgotSending(false);
      setForgotSent(true);
    }
  }

  function handleGoogleClick() {
    if (!window.google) {
      setError("Google sign-in isn\u2019t available right now. Try again in a moment.");
      return;
    }
    setError("");

    window.google.accounts.id.initialize({
      client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      callback: async (response) => {
        setError("");
        setLoading(true);
        try {
          const u = await loginWithGoogle(response.credential);
          navigate(destination(u), { replace: true });
        } catch (err) {
          setError("Something didn\u2019t work. Try again.");
        } finally {
          setLoading(false);
        }
      },
    });

    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        const el = document.getElementById("google-signin-btn");
        if (el) {
          window.google.accounts.id.renderButton(el, {
            theme: "outline",
            size: "large",
            width: 360,
          });
        }
      }
    });
  }

  return (
    <>
      <style>{STYLES}</style>
      <div className="auth-screen">

        {/* Ambient wash */}
        <div style={{
          position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)",
          width: 420, height: 380, pointerEvents: "none", zIndex: 0,
          background: "radial-gradient(ellipse at 50% 0%, rgba(158,132,86,0.10) 0%, transparent 68%)",
        }} />

        <div style={{ position: "relative", zIndex: 1, padding: "64px 28px 0", flex: 1 }}>

          {/* Brand */}
          <div className="fu d1" style={{ marginBottom: 40 }}>
            <Wordmark style={{ marginBottom: 16 }} />
            <h1 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 42, fontStyle: "italic", fontWeight: 400,
              color: P.text, lineHeight: 1.08, marginBottom: 12,
            }}>
              {mode === "login" ? (
                <>Welcome<br />back.</>
              ) : (
                <>Something<br />brought you here.</>
              )}
            </h1>
            <p style={{ fontSize: 13.5, color: P.textMute,
              fontWeight: 300, lineHeight: 1.65 }}>
              {mode === "login"
                ? "Your signal has been building. Pick up where you left off."
                : "Body. Mind. Behavior. Noticed daily."}
            </p>
          </div>

          {/* Mode tabs */}
          <div className="fu d2" style={{
            display: "flex", borderBottom: `1px solid ${P.border}`,
            marginBottom: 28,
          }}>
            {["login", "signup"].map(m => (
              <button key={m}
                className={`mode-tab${mode === m ? " active" : ""}`}
                onClick={() => { setMode(m); setError(""); }}>
                {m === "login" ? "Sign In" : "Sign Up"}
              </button>
            ))}
          </div>

          {/* OAuth — hidden in the iOS App Store binary. Offering Google
              there would require Sign in with Apple alongside it
              (Guideline 4.8); email/password alone triggers no such
              requirement. (Google GIS is also unreliable in WKWebView.) */}
          {!isNativeIOS() && (
            <>
              <div className="fu d3" style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
                <button id="google-signin-btn" className="btn-oauth" onClick={handleGoogleClick}>
                  <GoogleIcon />
                  Continue with Google
                </button>
              </div>

              {/* Divider */}
              <div className="fu d3" style={{
                display: "flex", alignItems: "center", gap: 14, marginBottom: 24,
              }}>
                <div style={{ flex: 1, height: 1, background: P.border }} />
                <span style={{ fontSize: 11, color: P.textFaint, letterSpacing: "0.1em" }}>or</span>
                <div style={{ flex: 1, height: 1, background: P.border }} />
              </div>
            </>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="fu d4" style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
              {mode === "signup" && (
                <div>
                  <div style={{ fontSize: 10, color: P.textMute, letterSpacing: "0.14em",
                    textTransform: "uppercase", fontWeight: 500, marginBottom: 7 }}>
                    Name
                  </div>
                  <input className="auth-input" type="text"
                    placeholder="First name" required
                    value={name} onChange={e => setName(e.target.value)} />
                </div>
              )}
              <div>
                <div style={{ fontSize: 10, color: P.textMute, letterSpacing: "0.14em",
                  textTransform: "uppercase", fontWeight: 500, marginBottom: 7 }}>
                  Email
                </div>
                <input className="auth-input" type="email"
                  placeholder="you@example.com" required
                  value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: 10, color: P.textMute, letterSpacing: "0.14em",
                  textTransform: "uppercase", fontWeight: 500, marginBottom: 7 }}>
                  Password
                </div>
                <input className="auth-input" type="password"
                  placeholder="" required minLength={8}
                  value={password} onChange={e => setPassword(e.target.value)} />
                {/* Forgot-password link — Sign In tab only. Signup can't
                    forget something it doesn't yet have. */}
                {mode === "login" && !forgotOpen && (
                  <button
                    type="button"
                    onClick={() => {
                      setForgotOpen(true);
                      setForgotEmail(email);
                      setForgotSent(false);
                      setError("");
                    }}
                    style={{
                      marginTop: 8,
                      background: "none", border: "none", padding: 0,
                      cursor: "pointer",
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 12, color: P.primary, fontWeight: 400,
                      letterSpacing: "0.02em",
                    }}
                  >
                    Forgot password?
                  </button>
                )}
              </div>

              {/* Invite / referral code — signup only. Collapsed to a
                  small link by default; expands to a single uppercase
                  input. A ?code= deep-link opens + pre-fills it on mount. */}
              {mode === "signup" && (
                <div>
                  {!codeOpen ? (
                    <button
                      type="button"
                      onClick={() => setCodeOpen(true)}
                      style={{
                        background: "none", border: "none", padding: 0,
                        cursor: "pointer",
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 12, color: P.primary, fontWeight: 400,
                        letterSpacing: "0.02em",
                      }}
                    >
                      Have an invite code? →
                    </button>
                  ) : (
                    <>
                      <div style={{ fontSize: 10, color: P.textMute, letterSpacing: "0.14em",
                        textTransform: "uppercase", fontWeight: 500, marginBottom: 7 }}>
                        Invite code
                      </div>
                      <input
                        className="auth-input"
                        type="text"
                        placeholder="Enter code"
                        autoCapitalize="characters"
                        autoCorrect="off"
                        spellCheck={false}
                        style={{ textTransform: "uppercase" }}
                        value={inviteCode}
                        onChange={e => setInviteCode(e.target.value.toUpperCase())}
                      />
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Inline forgot-password form — Sign In tab only */}
            {mode === "login" && forgotOpen && (
              <div className="fu d4" style={{
                padding: "16px 18px",
                background: P.surface,
                border: `1.5px solid ${P.border}`,
                borderRadius: 14,
                marginBottom: 16,
              }}>
                <div style={{
                  fontSize: 10, color: P.primary,
                  letterSpacing: "0.14em", textTransform: "uppercase",
                  fontWeight: 500, marginBottom: 10,
                }}>
                  Reset password
                </div>
                {forgotSent ? (
                  <p style={{
                    fontSize: 13, color: P.textMid, fontWeight: 300,
                    lineHeight: 1.6, fontStyle: "italic",
                  }}>
                    If that email exists, a reset link is on its way.
                  </p>
                ) : (
                  <>
                    <input
                      className="auth-input"
                      type="email"
                      placeholder="you@example.com"
                      required
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      style={{ marginBottom: 10 }}
                    />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        onClick={handleForgotSubmit}
                        disabled={forgotSending || !forgotEmail}
                        style={{
                          flex: 1, padding: "12px 16px",
                          background: P.primary,
                          color: "#F2EDE3",
                          border: "none", borderRadius: 10,
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: 13, fontWeight: 500,
                          letterSpacing: "0.04em",
                          cursor: forgotSending ? "default" : "pointer",
                          opacity: forgotSending || !forgotEmail ? 0.5 : 1,
                        }}
                      >
                        {forgotSending ? "Sending\u2026" : "Send reset link"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setForgotOpen(false)}
                        style={{
                          padding: "12px 14px",
                          background: "none",
                          border: `1.5px solid ${P.border}`,
                          borderRadius: 10,
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: 13, color: P.textMute, fontWeight: 400,
                          cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Invite-code failure — non-blocking. The account is already
                created and signed in; the code just didn't apply. Continue
                drops the user into the app on the standard trial path. */}
            {codeFailed && (
              <div className="fu d4" style={{
                padding: "14px 16px", borderRadius: 12,
                background: "rgba(158,132,86,0.08)",
                marginBottom: 16,
              }}>
                <p style={{ fontSize: 13, color: P.primary, fontWeight: 300, lineHeight: 1.55, marginBottom: 10 }}>
                  That code isn’t valid — you’ll start with a 7-day free trial instead.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setCodeFailed(false);
                    if (pendingUser) navigate(destination(pendingUser), { replace: true });
                  }}
                  style={{
                    padding: "10px 16px",
                    background: P.primary, color: "#F2EDE3",
                    border: "none", borderRadius: 10,
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 13, fontWeight: 500, letterSpacing: "0.04em",
                    cursor: "pointer",
                  }}
                >
                  Continue
                </button>
              </div>
            )}

            {/* Flash (e.g. post-reset success redirect) */}
            {flashMessage && (
              <div className="fu d4" style={{
                padding: "12px 16px", borderRadius: 12,
                background: P.primarySoft,
                marginBottom: 16,
              }}>
                <p style={{ fontSize: 13, color: P.primary, fontWeight: 400 }}>
                  {flashMessage}
                </p>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="fu d4" style={{
                padding: "12px 16px", borderRadius: 12,
                background: "rgba(158,132,86,0.08)",
                marginBottom: 16,
              }}>
                <p style={{ fontSize: 13, color: P.primary, fontWeight: 300 }}>{error}</p>
              </div>
            )}

            {/* Submit */}
            <div className="fu d5">
              <button className="btn-primary" type="submit"
                disabled={loading || !email || !password || (mode === "signup" && !name)}>
                {loading
                  ? (mode === "login" ? "Signing in\u2026" : "Creating account\u2026")
                  : (mode === "login" ? "Sign in" : "Create account")}
              </button>
            </div>
          </form>

          {/* Disclaimer */}
          <div className="fu d6" style={{ marginTop: 28, textAlign: "center" }}>
            <p style={{ fontSize: 11, color: P.textFaint, fontWeight: 300, lineHeight: 1.7 }}>
              Haiven does not diagnose, treat, or provide psychotherapy.
            </p>
          </div>

        </div>
      </div>
    </>
  );
}
